import { EntityList } from '../types/entityList'

/** The part of a persisted creditor this filter needs. */
export type CreditorEntityRef = {
  entity?: {
    entityType?: string | null
    displayEntityCode?: string | null
  } | null
}

/** The part of an in-progress form row this filter needs. */
export type PendingCreditorRef = {
  entity?: {
    entityType?: string | null
    entityName?: string | null
  } | null
}

/**
 * Entities that can still take a creditor on this wallet.
 *
 * A wallet holds at most one creditor per entity — `creditorRepository.Create` refuses
 * a duplicate with `ErrCreditorAlreadyExists`. The form offered every entity of the
 * chosen type regardless, so picking one that already had a creditor failed with that
 * message and no indication the existing creditor can simply be edited instead. On
 * stage that is the common case: every wallet carries an `adjusment` creditor from a
 * migration, and most carry a provider one from subscribe.
 *
 * Two sources of exclusion, and they cannot use the same key:
 *
 * **Persisted creditors — matched on `displayEntityCode`.** The BFF encrypts
 * `entityCode` and `entityID` with a random nonce (`rand.Read` in
 * `idcodec.encryptString`), so the same entity is a different ciphertext in every
 * response. Comparing either across two queries would never match, and this filter
 * would silently do nothing while looking correct. `displayEntityCode` is the plaintext
 * the codec deliberately skips.
 *
 * **Rows already filled in on this form — matched on `entityName`.** A row stores what
 * the picker gave it: `entityID` and `entityCode`, both encrypted, and this form
 * refetches the entity list on every change, so a row's stored ciphertext goes stale
 * against the current options. The name does not. The form already treats it as the
 * identity — its own `getOptionSelected` compares labels — so this matches what the
 * component does rather than introducing a second notion of sameness.
 *
 * **Scoped by entity type.** `creditorsBrief` returns creditors of every type while the
 * options are fetched for one, so an unscoped match would let a Channel creditor hide a
 * still-addable Provider entity if the two ever shared a code. Whether they can was not
 * verifiable — every creditor on stage is a Provider, so the data cannot answer it — and
 * scoping removes the question rather than betting on the answer.
 *
 * An exclusion with no resolvable key excludes nothing. Dropping an entity on a blank
 * key would hide a legitimately addable one, and a wrongly-missing option is worse than
 * a duplicate: the duplicate still fails safely at the server with a named error.
 */
export function entitiesWithoutACreditor(
  entities: EntityList[],
  existingCreditors: CreditorEntityRef[],
  entityType?: string | null,
  pendingRows: PendingCreditorRef[] = []
): EntityList[] {
  const sameType = (candidate?: string | null) =>
    !entityType || candidate === entityType

  const takenCodes = new Set(
    existingCreditors
      .filter((creditor) => sameType(creditor.entity?.entityType))
      .map((creditor) => creditor.entity?.displayEntityCode)
      .filter((code): code is string => Boolean(code))
  )

  const takenNames = new Set(
    pendingRows
      .filter((row) => sameType(row.entity?.entityType))
      .map((row) => row.entity?.entityName)
      .filter((name): name is string => Boolean(name))
  )

  return entities.filter(
    (entity) =>
      !takenCodes.has(entity.displayEntityCode) && !takenNames.has(entity.label)
  )
}
